import {
  Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, HostListener
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { AudioService }       from 'src/app/core/services/audio.service';
import { ApiService }         from 'src/app/core/services/api.service';
import { VideoShareService }  from 'src/app/core/services/video-share.service';
import { PlaylistAddService } from 'src/app/core/services/playlist-add.service';
import { UserService }        from 'src/app/core/services/user.service';
import { WatchTimeService }   from 'src/app/core/services/watch-time.service';
import { TranslateService }   from '@ngx-translate/core';
import { environment }        from 'src/environments/environment';

// hls.js is already in package.json (v1.5.17)
// We load it via angular.json scripts so no TypeScript import issues
declare var Hls: any;

/**
 * A saved position this close to the end is a track that was all but finished —
 * resuming there would play a moment of audio and stop, so it starts over.
 */
const RESUME_TAIL_SECONDS = 5;

@Component({
  selector: 'app-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss']
})
export class AudioPlayerComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Visibility ────────────────────────────────────────────────
  isPlayerOpen  = false;
  // The track page renders its own transport for this same player, so the bar
  // there would be a second set of controls for one <audio> — but only while the
  // track on screen is the one playing. Anything else needs the bar, since that
  // page's controls do not reach it. Kept separate from isPlayerOpen so leaving
  // the page restores whatever the player was doing.
  hideBar       = false;
  // Track id the current route shows its own controls for, null off that route.
  private ownControlsTrackId: any = null;

  // Free-listening limit prompt (Subscribe / Cancel)
  limitPopupOpen = false;

  // ── Playback state ────────────────────────────────────────────
  isPlaying     = false;
  isLoading     = false;
  isRadio       = false;

  // ── Track / queue ─────────────────────────────────────────────
  trackList:    any[]  = [];
  currentIndex: number = 0;
  currentTrack: any    = null;

  // ── Controls state ────────────────────────────────────────────
  repeatMode: 'off' | 'one' | 'all' = 'off';
  isShuffle      = false;
  volume         = 1;
  isMuted        = false;
  currentTime    = 0;
  duration       = 0;
  progressPct    = 0;   // 0-100, drives the CSS progress bar width
  playbackRate   = 1;
  readonly speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // ── Seekbar hover tooltip ─────────────────────────────────────
  hoverTime     = 0;
  hoverPct      = 0;
  showHoverTime = false;

  // ── Private ───────────────────────────────────────────────────
  private audio: HTMLAudioElement;
  private hls: any = null;
  private sendHistory = true;
  private subs: Subscription[] = [];
  private visibilityFn: () => void;

  // ── Played-time tracking ──────────────────────────────────────
  // Totals and API calls live in AudioService (shared with every play gate);
  // this component only measures how much was genuinely played, never seeked.
  private lastTickTime: any = null;    // last audio.currentTime seen by timeupdate
  private isSeeking         = false;

  // ── Resume position ───────────────────────────────────────────
  // Where the listener left the incoming track, parked by loadTrack() until the
  // element knows the track's duration. Only the native-HLS path uses it —
  // hls.js is told the start position up front instead. See applyPendingSeek().
  private pendingSeek = 0;

  // Set on mousedown, read by the click that follows — see onBarPress().
  private barPressOnControl = false;

  // ── Listen-activity reporting guards ──────────────────────────
  // loadTrack()'s own pause() fires an async 'pause' event after currentTrack
  // already points at the incoming track — that one must not re-report. And
  // overlapping triggers for the same moment (pause right before close/destroy,
  // beforeunload followed by pagehide) must not hit the API twice.
  private suppressPauseReport = false;
  private lastActivitySent: { id: any; seconds: number; at: number } | null = null;

  constructor(
    private audioService:    AudioService,
    private apiService:      ApiService,
    private router:          Router,
    private shareService:    VideoShareService,
    private playlistService: PlaylistAddService,
    private userService:     UserService,
    public  translate:       TranslateService,
    private cdr:             ChangeDetectorRef,
    private watchTime:       WatchTimeService
  ) {}

  // ─────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────

  // Tab reload/close — mirrors player.component.ts's beforeunload handler for
  // video, but uses the unload-safe transport: a normal HttpClient XHR gets
  // cancelled by the browser while the page tears down, so the activity call
  // silently never reached the server from here.
  @HostListener('window:beforeunload')
  onBeforeUnload() {
    this.reportAudioActivity(undefined, true);
    this.audioService.reportPlayedTime();
    // HttpClient calls are cancelled on unload; keepalive gets the last chunk out.
    this.watchTime.flush('audio', true);
  }

  // Mobile Safari/Android don't reliably fire beforeunload when a tab is
  // swiped away or discarded — pagehide is the event that actually comes.
  // On desktop reloads both fire; the dedupe in reportAudioActivity() keeps
  // the second one from double-posting.
  @HostListener('window:pagehide')
  onPageHide() {
    this.reportAudioActivity(undefined, true);
  }

  ngOnInit() {
    // Hand this instance to the service so pages that render their own transport
    // controls (the track page) drive this player rather than creating a second one.
    this.audioService.setAudioPlayer(this);

    // Hide the bar on that page and bring it back everywhere else. Only the bar
    // markup goes — the <audio> element is created in code, not in the template,
    // so playback carries on untouched while it is hidden.
    this.ownControlsTrackId = this.ownControlsRouteTrackId(this.router.url);
    this.updateHideBar();
    this.subs.push(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.ownControlsTrackId = this.ownControlsRouteTrackId(event.urlAfterRedirects || event.url);
          this.updateHideBar();
          this.cdr.detectChanges();
        }
      })
    );

    // Set up Audio element FIRST so loadTrack() can call this.audio.pause()
    // when apiData$ fires synchronously (BehaviorSubject emits on subscribe).
    // Previously this was in ngAfterViewInit which runs AFTER ngOnInit,
    // causing this.audio to be undefined when subscription #1 fired → crash →
    // subscriptions 2-4 (newState$, opencloseplayer$, setFavVar$) never registered.
    this.setupAudioElement();

    // Warm the subscription/free-limit figures up front. The player lives in the
    // footer on every page, so by the time a play button is clicked the
    // synchronous canPlayAudio() gate usually has real data to work with.
    this.audioService.refreshAudioLimit();

    // 1. New song / queue data
    this.subs.push(
      this.audioService.apiData$.subscribe(data => {
        if (data && localStorage.getItem('queueList')) {
          this.loadQueue(data);
        }
      })
    );

    // 2. External play / pause / dispose commands
    this.subs.push(
      this.audioService.newState$.subscribe(state => {
        if      (state === 'play')    { this.playAudio(); }
        else if (state === 'pause')   { this.pauseAudio(); }
        else if (state === 'dispose') { this.closePlayer(); }
      })
    );

    // 3. Close-player signal
    this.subs.push(
      this.audioService.opencloseplayer$.subscribe(val => {
        if (val === 0) { this.closePlayer(false); }
      })
    );

    // 4. Free-limit prompt, raised either by a play gate or mid-playback
    this.subs.push(
      this.audioService.limitPopup$.subscribe(open => {
        this.limitPopupOpen = !!open;
        this.cdr.detectChanges();
      })
    );

    // 5. External favourite toggle (from song-list pages)
    this.subs.push(
      this.audioService.setFavVar$.subscribe(src => {
        if (src && this.currentTrack && src === this.currentTrack.src) {
          this.currentTrack.favourite = !this.currentTrack.favourite;
          this.cdr.detectChanges();
        }
      })
    );
  }

  /**
   * Track id of a route that shows its own player controls, else null.
   *
   * Only the track page, /audio/:id. Matched on the segment count rather than a
   * prefix, because /audio, /audios, /audio-search and /audio-homepage all start
   * the same way and all still want the bar, as does the three-segment
   * /audio/songs/:song_slug.
   */
  private ownControlsRouteTrackId(url: string): any {
    if (!url) { return null; }
    const segments = url.split('?')[0].split('/').filter(part => !!part);
    return (segments.length === 2 && segments[0] === 'audio') ? segments[1] : null;
  }

  /** Hidden only where the page on screen is the page for the playing track. */
  private updateHideBar() {
    this.hideBar = !!this.ownControlsTrackId
      && !!this.currentTrack
      && String(this.currentTrack.id) === String(this.ownControlsTrackId);
  }

  ngAfterViewInit() {
    // setupAudioElement() moved to ngOnInit (must run before subscriptions)
  }

  // ─────────────────────────────────────────────────────────────
  // Audio element setup (runs once)
  // ─────────────────────────────────────────────────────────────

  private setupAudioElement() {
    this.audio          = new Audio();
    this.audio.preload  = 'metadata';

    this.audio.addEventListener('timeupdate', () => {
      this.accumulatePlayedTime();
      this.currentTime = this.audio.currentTime;
      this.progressPct = this.duration > 0
        ? (this.currentTime / this.duration) * 100
        : 0;
      this.audioService.updatePlayerProgress({
        currentTime: this.currentTime,
        duration:    this.duration
      });
      this.cdr.detectChanges();
    });

    this.audio.addEventListener('loadedmetadata', () => {
      this.duration  = isFinite(this.audio.duration) ? this.audio.duration : 0;
      this.isLoading = false;
      this.applyPendingSeek();
      this.cdr.detectChanges();
    });

    // Seeking must not count as listening: drop the reference point while a seek
    // is in flight and re-anchor it once the element settles at the new position.
    this.audio.addEventListener('seeking', () => {
      this.isSeeking    = true;
      this.lastTickTime = null;
    });

    this.audio.addEventListener('seeked', () => {
      this.isSeeking    = false;
      this.lastTickTime = this.audio.currentTime;
    });

    this.audio.addEventListener('playing', () => {
      this.isPlaying = true;
      this.isLoading = false;
      // A track change that never fired its 'pause' event (element was already
      // paused) leaves the suppress flag armed — clear it so the next genuine
      // user pause still reports.
      this.suppressPauseReport = false;
      // Re-anchor after any stall/resume so buffered-but-not-played time is skipped.
      this.lastTickTime = this.audio.currentTime;
      this.audioService.refreshAudioLimit();
      this.enforceFreeLimit();
      document.body.classList.add('audio-playing', 'audio-started');
      this.audioService.songState('play');
      this.audioService.setStatus(this.currentTrack);

      if (this.currentTrack) {
        if (this.currentTrack.is_radio) {
          // IMPORTANT: Do NOT call setCurrentPlayingRadioId() here.
          // radio.component.ts already set it to the ngFor loop index before
          // calling setData(). audio-playlist-card compares
          // getCurrentPlayingRadioId() === currentIndex (loop index), so
          // overwriting with this.currentTrack.id (e.g. 4034007) breaks the
          // graph comparison and hides the waveform on every resume.
          this.audioService.setCurrentPlayingSongId(null);
          // Re-emit track object so the card's songState$→'play' handler calls
          // updateRadioIndex() with the still-correct loop index → graph shows.
          this.audioService.newStateSong(this.currentTrack);
        } else {
          this.audioService.setCurrentPlayingSongId(this.currentTrack.id);
          this.audioService.setCurrentPlayingRadioId(null);
        }
      }

      // Send play-history once per track
      if (this.sendHistory && this.currentTrack) {
        this.sendHistory = false;
        this.apiService.callPostAPI('v2/audio-play-history', {
          audio: this.currentTrack.slug
        }).subscribe(
          () => {},
          err => this.apiService.failureResponse(err)
        );
      }
      this.cdr.detectChanges();
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this.isLoading = false;  // clear spinner on any pause — play button must show
      document.body.classList.remove('audio-playing');
      this.lastTickTime = null;
      this.audioService.reportPlayedTime();
      this.audioService.newStateSong('pause');
      this.audioService.songState('pause');
      // Save listening position on every pause so progress is never lost —
      // except when the pause came from loadTrack() swapping tracks (already
      // reported there, and currentTrack now points at the incoming track), or
      // from natural end ('ended' fires next and reports 0, the fully-played
      // marker this endpoint expects).
      if (this.suppressPauseReport) {
        this.suppressPauseReport = false;
      } else if (!this.audio.ended) {
        this.reportAudioActivity();
      }
      this.cdr.detectChanges();
    });

    this.audio.addEventListener('ended', () => {
      this.onTrackEnded();
    });

    this.audio.addEventListener('waiting', () => {
      this.isLoading = true;
      // Buffering is not listening — stop accumulating until 'playing' fires again.
      this.lastTickTime = null;
      this.cdr.detectChanges();
    });

    this.audio.addEventListener('canplay', () => {
      this.isLoading = false;
      this.cdr.detectChanges();
    });

    // Defensive: if hls.js recoverMediaError() calls media.load() it resets playbackRate to 1.
    // Detect the reset and restore our stored rate immediately.
    this.audio.addEventListener('ratechange', () => {
      if (!this.isRadio && this.playbackRate !== 1 &&
          this.audio.playbackRate !== this.playbackRate) {
        this.audio.playbackRate = this.playbackRate;
      }
    });

    this.audio.addEventListener('error', () => {
      this.isLoading = false;
      console.error('[AudioPlayer] Error on track:', this.currentTrack ? this.currentTrack.src : '');
      this.cdr.detectChanges();
    });

    // ── Page Visibility API ──────────────────────────────────────
    // When user comes back to the tab the native <audio> element
    // resumes automatically; we just ensure it was not paused by
    // the browser mid-stream.
    this.visibilityFn = () => {
      if (!document.hidden && this.isPlaying && this.audio.paused) {
        this.audio.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', this.visibilityFn);
  }

  // ─────────────────────────────────────────────────────────────
  // Queue loading
  // ─────────────────────────────────────────────────────────────

  private loadQueue(data: any) {
    const mapTrack = (val: any) => ({
      id:                 val.id,
      src:                val.hls_playlist_url || val.src,
      type:               'application/x-mpegURL',
      title:              val.audio_title   || val.title,
      authorTitle:        val.artist_name   || val.authorTitle,
      thumbnail:          val.audio_thumbnail || val.thumbnails,
      // Injected by radio.component.ts playlistClick() into each track:
      playlist_name:      val.playlist_name      || null,
      playlist_thumbnail: val.playlist_thumbnail || null,
      favourite:          val.is_favourite ? true : false,
      slug:               val.slug,
      album_name:         val.album_name,
      // Carried so shareTrack() and openTrackPage() can put ?albumId= in the
      // link. Some lists send the album nested instead of as a flat id, and the
      // track page cannot resolve a track without it, so both shapes are taken.
      album_id:           val.album_id || (val.album && val.album.id) || null,
      is_radio:           val.is_radio || false,
      radio_name:         val.is_radio ? val.radio_name : null,
      // Continue Listening rows carry where the listener stopped. `seconds` is
      // zeroed server-side once a track has been heard to the end (see
      // AudioWatchRepository::addAudioWatch), so it doubles as the "not
      // finished" marker — a finished track starts from the top again.
      resume_at:          this.resumePosition(val.activity, this.durationToSeconds(val.audio_duration))
    });

    if (data.selectedindex !== null) {
      // Full playlist replace
      this.trackList    = (data.songList || []).map(mapTrack);
      this.currentIndex = data.selectedindex;
      this.isPlayerOpen = true;
      this.audioService.setQueueData(this.trackList);

      // If the same non-radio track is already paused mid-way (e.g. paused by the
      // video player), skip full HLS reinitialisation — just refresh metadata so
      // audio.play() later resumes from the exact paused position.
      const newSrc = this.trackList[this.currentIndex] ? this.trackList[this.currentIndex].src : null;
      const curSrc = this.currentTrack ? this.currentTrack.src : null;
      if (
        newSrc && newSrc === curSrc &&
        this.audio && this.audio.paused && this.audio.currentTime > 0 &&
        this.currentTrack && !this.currentTrack.is_radio
      ) {
        this.currentTrack = { ...this.trackList[this.currentIndex] };
        this.updateHideBar();
        this.cdr.detectChanges();
        return;  // preserve currentTime — don't reload from scratch
      }

      this.sendHistory  = true;
      this.loadTrack(this.currentIndex);
    } else {
      // Add single song to existing queue
      if (data.songList) {
        this.trackList.push(mapTrack(data.songList));
        this.audioService.setQueueData(this.trackList);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Load & start a specific track by index
  // ─────────────────────────────────────────────────────────────

  private loadTrack(index: number) {
    if (!this.trackList || !this.trackList[index]) { return; }

    // Track change — flush what was listened to on the outgoing track first,
    // BEFORE currentTrack is swapped below (the report reads currentTrack.id
    // and audio.currentTime, both still the outgoing track's here).
    this.lastTickTime = null;
    this.audioService.reportPlayedTime();
    // Skip when the outgoing track ended naturally: onTrackEnded() already
    // reported the fully-played marker (seconds: 0), and currentTime here
    // still reads as the track's duration — sending it would overwrite that 0.
    if (this.audio && !this.audio.ended) {
      this.reportAudioActivity();
    }
    // The pause() below fires its 'pause' event asynchronously, after
    // currentTrack already points at the incoming track — suppress that one
    // report. Only arm the flag when a pause event will actually fire
    // (pausing an already-paused element fires nothing, and an armed flag
    // would swallow the user's next genuine pause).
    this.suppressPauseReport = this.audio && !this.audio.paused;

    this.currentIndex = index;
    this.currentTrack = { ...this.trackList[index] };  // shallow copy so mutations don't affect the list
    // One-shot: the saved position is where the listener left off *last time*,
    // so it is spent the moment it is used. Cleared on the queue entry too —
    // currentTrack is only a copy of it — or repeat would keep dropping the
    // playhead back there on every lap.
    const resumeAt = Math.max(0, Number(this.currentTrack.resume_at) || 0);
    this.trackList[index].resume_at = 0;
    this.currentTrack.resume_at     = 0;
    // Cleared for every path, not just the one that sets it below: a load that
    // never reaches 'loadedmetadata' would otherwise leave a stale position
    // armed for the next track.
    this.pendingSeek = 0;
    this.updateHideBar();
    this.isRadio      = !!this.currentTrack.is_radio;
    this.currentTime  = 0;
    this.duration     = 0;
    this.progressPct  = 0;
    this.isLoading    = true;
    this.sendHistory  = true;

    // Destroy old HLS instance
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    this.audio.pause();

    const src = this.currentTrack.src;

    if (typeof Hls !== 'undefined' && Hls.isSupported()) {
      this.hls = new Hls({
        maxBufferLength:    30,
        maxMaxBufferLength: 60,
        enableWorker:       true,
        // Loading straight from the saved position rather than starting at zero
        // and seeking: a seek after play() would sound the opening second of the
        // track before jumping. -1 is hls.js's own "from the beginning".
        startPosition:      resumeAt > 0 ? resumeAt : -1
      });

      this.hls.loadSource(src);
      this.hls.attachMedia(this.audio);

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.audio.playbackRate = this.playbackRate;
        this.audio.play().catch(e => console.error('[AudioPlayer] play() blocked:', e));
      });

      this.hls.on(Hls.Events.ERROR, (_evt: any, data: any) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // Try to recover from network error
              this.hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              this.hls.recoverMediaError();
              break;
            default:
              this.hls.destroy();
              break;
          }
        }
      });

    } else if (this.audio.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari — native HLS support. No start-position option here, so the
      // position waits for metadata; currentTime cannot be set before the
      // element knows the duration.
      this.pendingSeek = resumeAt;
      this.audio.src = src;
      this.audio.play().catch(e => console.error('[AudioPlayer] play() blocked:', e));
    } else {
      console.error('[AudioPlayer] HLS not supported in this browser.');
    }

    this.cdr.detectChanges();
  }

  /**
   * Where to drop the playhead, in seconds — 0 when there is nothing worth
   * resuming. Takes the `activity` a queue row carried and the track's length
   * where that is known. Only two things put an activity on a row: the Continue
   * Listening list, and the track page when it was opened from that list.
   *
   * `current_seconds` is the last position reported for this listener. Its
   * sibling `seconds` is zeroed server-side once the track has been heard to the
   * end (AudioWatchRepository::addAudioWatch), so it reads as "finished" and the
   * track starts over rather than resuming a second before it ends. The length
   * guards the same thing for a position the server has not marked yet.
   */
  private resumePosition(activity: any, total: number): number {
    if (!activity || Number(activity.seconds) <= 0) { return 0; }

    const at = Math.floor(Number(activity.current_seconds) || 0);
    if (at <= 0) { return 0; }

    if (total > 0 && at >= total - RESUME_TAIL_SECONDS) { return 0; }
    return at;
  }

  /** 'MM:SS' or 'HH:MM:SS' to seconds; 0 for anything else. */
  private durationToSeconds(value: any): number {
    if (!value || typeof value !== 'string') { return 0; }
    const parts = value.split(':').map(part => Number(part));
    if (parts.some(part => isNaN(part))) { return 0; }
    if (parts.length === 3) { return (parts[0] * 3600) + (parts[1] * 60) + parts[2]; }
    if (parts.length === 2) { return (parts[0] * 60) + parts[1]; }
    return 0;
  }

  /**
   * The native-HLS half of resuming (Safari): currentTime cannot be set before
   * the element knows the duration, so loadTrack() parks the position and this
   * drops the playhead there the moment metadata lands. hls.js never reaches
   * here — it is handed the start position up front.
   */
  private applyPendingSeek() {
    const at = this.pendingSeek;
    this.pendingSeek = 0;
    if (at <= 0) { return; }
    if (this.duration && at >= this.duration - RESUME_TAIL_SECONDS) { return; }

    this.audio.currentTime = at;
    this.currentTime       = at;
    this.progressPct       = this.duration > 0 ? (at / this.duration) * 100 : 0;
  }

  // ─────────────────────────────────────────────────────────────
  // Player controls
  // ─────────────────────────────────────────────────────────────

  playAudio() {
    if (this.audio && this.audio.src) {
      this.audio.play().catch(e => console.error('[AudioPlayer]', e));
    }
  }

  pauseAudio() {
    if (this.audio) { this.audio.pause(); }
  }

  togglePlayPause() {
    this.isPlaying ? this.pauseAudio() : this.playAudio();
  }

  nextTrack() {
    this.sendHistory = true;
    if (this.isShuffle) {
      let r: number;
      do { r = Math.floor(Math.random() * this.trackList.length); }
      while (r === this.currentIndex && this.trackList.length > 1);
      this.loadTrack(r);
    } else {
      this.loadTrack((this.currentIndex + 1) % this.trackList.length);
    }
  }

  prevTrack() {
    this.sendHistory = true;
    if (this.audio.currentTime > 3) {
      // Restart current track if more than 3 s played
      this.lastTickTime = null;
      this.audio.currentTime = 0;
    } else {
      const prev = (this.currentIndex - 1 + this.trackList.length) % this.trackList.length;
      this.loadTrack(prev);
    }
  }

  skipForward() {
    if (this.audio && !this.isRadio) {
      this.lastTickTime = null;
      this.audio.currentTime = Math.min(this.audio.currentTime + 10, this.duration);
    }
  }

  skipBackward() {
    if (this.audio && !this.isRadio) {
      this.lastTickTime = null;
      this.audio.currentTime = Math.max(this.audio.currentTime - 10, 0);
    }
  }

  /**
   * Called when user clicks anywhere on the progress bar.
   * Seek bar — supports click, mouse drag, and touch drag.
   */

  // ── Seek: mouse ──────────────────────────────────────────────
  onProgressMouseDown(event: MouseEvent) {
    if (!this.audio || this.isRadio || !this.duration) { return; }
    event.preventDefault();
    this.showHoverTime = false;   // hide tooltip while dragging
    const bar = event.currentTarget as HTMLElement;

    this.seekFromClientX(bar, event.clientX);

    const onMove = (e: MouseEvent) => { this.seekFromClientX(bar, e.clientX); };
    const onUp   = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ── Seek: touch ──────────────────────────────────────────────
  onProgressTouchStart(event: TouchEvent) {
    if (!this.audio || this.isRadio || !this.duration) { return; }
    const bar = event.currentTarget as HTMLElement;

    this.seekFromClientX(bar, event.touches[0].clientX);

    const onMove = (e: TouchEvent) => { this.seekFromClientX(bar, e.touches[0].clientX); };
    const onEnd  = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend',  onEnd);
    };
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend',  onEnd);
  }

  // ── Seek helper ───────────────────────────────────────────────
  private seekFromClientX(bar: HTMLElement, clientX: number) {
    const rect  = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    // A drag scrub is a stream of tiny forward jumps — drop the anchor on every
    // one so none of them are mistaken for playback.
    this.lastTickTime       = null;
    this.progressPct        = ratio * 100;
    this.audio.currentTime  = ratio * this.duration;
    this.cdr.detectChanges();
  }

  onVolumeChange(event: any) {
    const val = parseFloat(event.target.value);
    this.volume      = val;
    this.audio.volume = val;
    this.isMuted     = val === 0;
  }

  toggleMute() {
    this.isMuted          = !this.isMuted;
    this.audio.muted      = this.isMuted;
    this.volume           = this.isMuted ? 0 : 1;
    this.audio.volume     = this.volume;
  }

  toggleShuffle() {
    this.isShuffle = !this.isShuffle;
  }

  cycleRepeat() {
    const modes: Array<'off' | 'one' | 'all'> = ['all', 'one', 'off'];
    const i = modes.indexOf(this.repeatMode);
    this.repeatMode = modes[(i + 1) % modes.length];
  }

  cycleSpeed() {
    if (this.isRadio) { return; }
    const i = this.speedOptions.indexOf(this.playbackRate);
    this.playbackRate = this.speedOptions[(i + 1) % this.speedOptions.length];
    if (this.audio) { this.audio.playbackRate = this.playbackRate; }
  }

  // Called from (ngModelChange) on the speed <select> — receives the number value directly.
  onSpeedChange(rate: number) {
    if (this.isRadio) { return; }
    this.playbackRate = rate;
    if (this.audio) { this.audio.playbackRate = this.playbackRate; }
  }

  // ── Seekbar hover tooltip ─────────────────────────────────────

  onProgressMouseEnter() {
    if (this.isRadio || !this.duration) { return; }
    this.showHoverTime = true;
    this.cdr.detectChanges();
  }

  onProgressMouseLeave() {
    this.showHoverTime = false;
    this.cdr.detectChanges();
  }

  onProgressHover(event: MouseEvent) {
    if (!this.showHoverTime || this.isRadio || !this.duration) { return; }
    const bar  = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    this.hoverPct  = ratio * 100;
    this.hoverTime = ratio * this.duration;
    this.cdr.detectChanges();
  }

  // ─────────────────────────────────────────────────────────────
  // Track ended
  // ─────────────────────────────────────────────────────────────

  private onTrackEnded() {
    this.lastTickTime = null;
    this.audioService.reportPlayedTime();

    // is_radio only marks "came from the playlists_details endpoint" (see
    // Audios::getIsRadioAttribute in media-api) — these are finite tracks in a
    // queue, not a live stream, so a radio playlist must keep playing too.
    // The repeat/shuffle controls are hidden in radio mode, so modes carried
    // over from a prior audio session must not affect it: always advance
    // sequentially and wrap around at the end so the radio keeps going.
    if (this.isRadio) {
      if (this.trackList.length > 1) {
        this.sendHistory = true;
        this.loadTrack((this.currentIndex + 1) % this.trackList.length);
      }
      return;
    }

    // Track finished naturally — report it as fully played (seconds: 0, same
    // convention player.component.ts uses on video 'ended') before we advance
    // to the next track / reset state.
    this.reportAudioActivity(0);

    if (this.repeatMode === 'one') {
      this.audio.currentTime = 0;
      this.audio.play();
    } else if (
      this.isShuffle ||
      this.repeatMode === 'all' ||
      this.currentIndex < this.trackList.length - 1
    ) {
      this.nextTrack();
    } else {
      // End of playlist, no repeat
      this.isPlaying = false;
      document.body.classList.remove('audio-playing');
      this.audioService.newStateSong('dispose');
      this.audioService.songState('pause');
      this.cdr.detectChanges();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Favourite / Share / Playlist
  // ─────────────────────────────────────────────────────────────

  toggleFavourite() {
    if (!localStorage.getItem('currentUser')) {
      this.userService.displayPopup('sign-in');
      return;
    }
    if (!this.currentTrack) { return; }

    const slug = this.currentTrack.slug;

    if (!this.currentTrack.favourite) {
      this.apiService.callPostAPI('v2/audio-favourite?slug=' + slug, {}).subscribe(
        res => {
          this.apiService.successResponse(res);
          this.currentTrack.favourite         = true;
          this.trackList[this.currentIndex].favourite = true;
        },
        err => this.apiService.failureResponse(err)
      );
    } else {
      this.apiService.callPutAPI('v2/audio-favourite?slug=' + slug, {}).subscribe(
        res => {
          this.apiService.successResponse(res);
          this.currentTrack.favourite         = false;
          this.trackList[this.currentIndex].favourite = false;
        },
        err => this.apiService.failureResponse(err)
      );
    }
  }

  /**
   * Title click → the track page for whatever is playing.
   *
   * That page resolves a track through its album (there is no per-id track
   * endpoint on web), so a track carrying no album_id would only land on a Not
   * Found — and radio has no track page at all. Both are left as plain text
   * rather than dressed up as a link that goes nowhere.
   */
  canOpenTrackPage() {
    return !!(this.currentTrack && this.currentTrack.id && this.currentTrack.album_id && !this.isRadio);
  }

  /**
   * Remembers whether a press started on a control, because that is what tells a
   * scrub apart from a click on the bar: a seek or volume drag released over the
   * track info delivers its click to the bar itself, and navigating out from
   * under the listener there would be the last thing they asked for.
   */
  onBarPress(event) {
    this.barPressOnControl = this.isBarControl(event.target);
  }

  /**
   * A click anywhere on the bar opens the track page — the strip as a whole is
   * the track, not just its title. A click that landed on a control has already
   * done what it came to do.
   */
  onBarClick(event) {
    const startedOnControl = this.barPressOnControl;
    this.barPressOnControl = false;

    if (startedOnControl) { return; }
    if (!this.canOpenTrackPage()) { return; }
    if (this.isBarControl(event.target)) { return; }

    this.openTrackPage();
  }

  /**
   * Whether a node inside the bar is (or sits inside) something that handles its
   * own clicks. Walked by hand rather than with closest(): the icons are SVG
   * children, and their class list is not reliably readable the way an HTML
   * element's is.
   */
  private isBarControl(node: any): boolean {
    let el = node;
    while (el && el.nodeType === 1) {
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'button' || tag === 'a' || tag === 'input' || tag === 'select') { return true; }

      const cls = ' ' + ((el.getAttribute && el.getAttribute('class')) || '') + ' ';
      if (cls.indexOf(' ap-progress ') > -1) { return true; }
      // Reached the bar itself without meeting one.
      if (cls.indexOf(' ap-bar ') > -1) { return false; }

      el = el.parentNode;
    }
    return false;
  }

  openTrackPage() {
    if (!this.canOpenTrackPage()) { return; }
    // The bar hides itself once that page is showing the playing track — see
    // updateHideBar() — so the page's own transport takes over from here.
    this.router.navigate(['/audio', this.currentTrack.id], {
      queryParams: { albumId: this.currentTrack.album_id }
    });
  }

  shareTrack() {
    if (!this.currentTrack) { return; }

    // Build a share data object that matches what header.component.ts expects
    // from a video share, plus the isaudio flag for the /audio/ URL path.
    const shareData = {
      id:              this.currentTrack.id,
      slug:            this.currentTrack.slug,
      title:           this.currentTrack.title,
      album_id:        this.currentTrack.album_id,
      // header.component.ts uses thumbnail_image; our track stores it as thumbnail
      thumbnail_image: this.currentTrack.thumbnail || 'assets/images/blank_new.png',
      // description triggers og:description meta and some popup conditionals
      description:     this.currentTrack.title,
      // flags — false so share popup does not hide its action buttons
      iswebseries:     false,
      is_story:        false,
      isaudio:         true    // tells header to use /audio/ in the share URL
    };

    this.shareService.displayPopup(shareData);
    this.shareService.displayPopupBlur(2);
  }

  addToPlaylist() {
    if (this.currentTrack) {
      this.playlistService.displayPopup(this.currentTrack);
      this.shareService.displayPopupBlur(2);
    }
  }

  closePlayer(removeQueue = true) {
    // Report progress on whatever was playing before we tear down state below.
    this.reportAudioActivity();
    this.lastTickTime = null;
    this.audioService.reportPlayedTime();

    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
    }
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    this.isPlayerOpen = false;
    this.isPlaying    = false;
    this.trackList    = [];
    this.currentTrack = null;
    this.updateHideBar();
    this.currentTime  = 0;
    this.duration     = 0;
    this.progressPct  = 0;

    this.audioService.newStateSong('pause');
    this.audioService.songState('pause');
    this.audioService.setCurrentPlayingSongId(null);
    this.audioService.setCurrentPlayingRadioId(null);
    document.body.classList.remove('audio-started', 'audio-playing');

    if (removeQueue) { localStorage.removeItem('queueList'); }
    this.cdr.detectChanges();
  }

  // ─────────────────────────────────────────────────────────────
  // Utility
  // ─────────────────────────────────────────────────────────────

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) { return '00:00:00'; }
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const hh = (h < 10 ? '0' : '') + h;
    const mm = (m < 10 ? '0' : '') + m;
    const ss = (s < 10 ? '0' : '') + s;
    return hh + ':' + mm + ':' + ss;
  }

  // ─────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────

  ngOnDestroy() {
    this.audioService.setAudioPlayer(null);
    this.reportAudioActivity();
    this.audioService.reportPlayedTime();
    this.subs.forEach(s => s.unsubscribe());
    if (this.audio) { this.audio.pause(); this.audio.src = ''; }
    if (this.hls)   { this.hls.destroy(); }
    document.removeEventListener('visibilitychange', this.visibilityFn);
  }

  // ─────────────────────────────────────────────────────────────
  // Watch/listen activity tracking (users/api/v2/audio/activity)
  // ─────────────────────────────────────────────────────────────

  /**
   * Reports listening progress for the current track to users-api, so playback
   * position can be resumed later. Mirrors player.component.ts's
   * storeWatchedDuration()/videoanalytics calls for video.
   *
   * @param seconds Position to report. Omit to use the audio element's current
   *   playback position (used on pause/close/reload/destroy). Pass 0 explicitly
   *   when a track finishes naturally (matches the video player's 'ended'
   *   convention).
   * @param unloadSafe Send via fetch keepalive instead of HttpClient — the only
   *   transport the browser lets finish once the page starts unloading. Use
   *   from beforeunload/pagehide only.
   */
  private reportAudioActivity(seconds?: number, unloadSafe = false) {
    // Radio streams don't correspond to a row in the `audios` table, so
    // audio_id would fail the backend's `exists:audios,id` validation.
    if (!this.currentTrack || this.isRadio) { return; }
    // The endpoint requires an authenticated user (auth()->user()->id server
    // side) — skip for anonymous listeners instead of triggering a 401.
    if (!localStorage.getItem('currentUser')) { return; }

    let position: number;
    if (seconds !== undefined) {
      // Explicit value from the caller (e.g. 0 on natural track-end) — always send it.
      position = seconds;
    } else {
      position = this.audio ? this.audio.currentTime : 0;
      if (!position || position <= 0) { return; } // nothing meaningful played yet
    }

    const secondsInt = Math.floor(position);

    // Several triggers can fire for the same moment (a 'pause' event right
    // before closePlayer/ngOnDestroy, beforeunload followed by pagehide on a
    // reload) — an identical record again within a few seconds saves nothing
    // new, so skip the duplicate.
    if (this.lastActivitySent &&
        this.lastActivitySent.id === this.currentTrack.id &&
        this.lastActivitySent.seconds === secondsInt &&
        Date.now() - this.lastActivitySent.at < 3000) {
      return;
    }
    this.lastActivitySent = { id: this.currentTrack.id, seconds: secondsInt, at: Date.now() };

    const payload = { audio_id: this.currentTrack.id, seconds: secondsInt };

    if (unloadSafe) {
      this.apiService.callUnloadSafePostAPI('v2/audio/activity', payload, environment.userApiUrl);
      return;
    }

    this.apiService.callPostAPI('v2/audio/activity', payload, environment.userApiUrl).subscribe(
      () => {},
      err => this.apiService.failureResponse(err)
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Played-time tracking + free-limit enforcement
  // ─────────────────────────────────────────────────────────────

  /**
   * Hands the media time that elapsed since the previous timeupdate to
   * AudioService. Only small forward steps are accepted: a timeupdate fires
   * roughly every 250 ms, so a genuine step is ~0.25 s × playbackRate, while a
   * seek (10 s skip, progress-bar click, live-window slide) produces a jump far
   * beyond that and a backward seek produces a negative one. Both are discarded,
   * so scrubbing can never inflate the counter. `lastTickTime === null` means the
   * anchor was deliberately dropped (seek in flight, buffering, pause, track
   * change) — that tick only re-anchors and contributes nothing.
   */
  private accumulatePlayedTime() {
    if (!this.audio || this.isSeeking || this.audio.paused) {
      this.lastTickTime = null;
      return;
    }

    const now = this.audio.currentTime;
    if (this.lastTickTime !== null) {
      const delta   = now - this.lastTickTime;
      const maxStep = 2 * (this.playbackRate || 1);
      if (delta > 0 && delta < maxStep) {
        this.audioService.addPlayedSeconds(delta);
      }
    }
    this.lastTickTime = now;

    this.enforceFreeLimit();
  }

  /**
   * Stops a non-subscriber the moment the free allowance runs out. This is the
   * only enforcement point that cannot be bypassed — every play path funnels
   * through the player, so the per-page gates only have to avoid prompting
   * early. Playback is paused and the remaining seconds posted, then the
   * Subscribe / Cancel prompt decides what happens to the player.
   */
  private enforceFreeLimit() {
    if (this.limitPopupOpen || !this.audioService.isLimitReached()) { return; }

    this.lastTickTime = null;
    if (this.audio) { this.audio.pause(); }
    this.audioService.reportPlayedTime();
    this.audioService.openLimitPopup();
  }

  /** Subscribe button — leave the player behind and go to the plans page. */
  onLimitSubscribe() {
    this.audioService.closeLimitPopup();
    this.closePlayer();
    this.audioService.redirectToSubscription();
  }

  /** Cancel button (and backdrop click) — just shut the player down. */
  onLimitCancel() {
    this.audioService.closeLimitPopup();
    this.closePlayer();
  }
}
